import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import enviro from 'enviro';
import PortalIdParser from 'PortalIdParser';
import { INSERT_HUBSPOT_VIDEO } from 'rich-text-lib/constants/usageTracking';
import { userInfoSync } from 'hub-http/userInfo';
import { callIfPossible } from 'UIComponents/core/Functions';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Tile } from 'foundations-components/transitional/components/Tile';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tracker } from '../utils/usageTracker';
import { INLINE_VIDEO_CONSTANTS, VIDEO_CONSTANTS } from './utils/constants';
import { buildShardedVideoShareUrl, isDefaultHubSpotHostedUrl } from './utils/createGoVideoUrl';
import { encodeUrl } from './utils/libUtils';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  DEFAULT_VIDEO_WIDTH,
  DRAFT_ATOMIC_TYPE_VIDEO,
  EMBED_SCRIPT_URL,
  SALES_PROD_CLIENT_ID,
  SALES_QA_CLIENT_ID,
  SERVICE_HUB_PROD_CLIENT_ID,
  SERVICE_HUB_QA_CLIENT_ID
} = VIDEO_CONSTANTS;
const {
  DRAFT_ATOMIC_TYPE_INLINE_VIDEO
} = INLINE_VIDEO_CONSTANTS;
const VIDEO_DOMAIN_SHARDING_GATE = 'DAM:1to1Video:DomainSharding';
function getClientId(isInServiceHub) {
  const isProd = enviro.isProd();
  if (isInServiceHub) {
    return isProd ? SERVICE_HUB_PROD_CLIENT_ID : SERVICE_HUB_QA_CLIENT_ID;
  }
  return isProd ? SALES_PROD_CLIENT_ID : SALES_QA_CLIENT_ID;
}
class GoVideoContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.isVideoPageDomainShardingUngated = () => {
      return enviro.ifHublet({
        isNa1: () => {
          try {
            const auth = userInfoSync();
            return auth.gates.includes(VIDEO_DOMAIN_SHARDING_GATE);
          } catch (err) {
            console.warn('userInfoSync threw error, assuming video page domain sharding is not ungated');
            return false;
          }
        },
        isNonNa1: () => {
          return true;
        }
      });
    };
    this.handleInitiateVidyardComponent = () => {
      const {
        recipientEmail
      } = this.props;
      const vidyardInstance = window.Vidyard;
      if (vidyardInstance) {
        if (this.initiateComponentTimeout) {
          clearTimeout(this.initiateComponentTimeout);
        }
        this.setState({
          isLoading: false
        });
        const clientId = getClientId(this.props.isInServiceHub);
        const library = vidyardInstance.goVideo.createLibrary(document.getElementById('GoVideo'), {
          clientId,
          recipientEmail
        });
        library.on('player:created', this.handleConfirm);
      } else {
        this.initiateComponentTimeout = setTimeout(this.handleInitiateVidyardComponent, 100);
      }
    };
    this.handleConfirm = ({
      player: {
        custom_id,
        name,
        uuid
      },
      embed_codes: {
        sharing_page,
        thumbnail,
        base_url
      },
      videos
    }) => {
      const {
        onChange,
        onClose,
        onConfirm,
        trackingHandlers,
        isInlineVideo
      } = this.props;
      if (onConfirm) {
        const url = this.isVideoPageDomainShardingUngated() && isDefaultHubSpotHostedUrl(sharing_page) ? buildShardedVideoShareUrl(uuid) : sharing_page;
        onConfirm({
          player: {
            custom_id,
            sharing_page: url
          },
          videos
        });
        return;
      }
      const url = this.isVideoPageDomainShardingUngated() && isDefaultHubSpotHostedUrl(sharing_page) ? buildShardedVideoShareUrl(uuid, {
        appendPortalId: true
      }) : `${sharing_page}portalId=${PortalIdParser.get()}`;
      const videoBlockMetadata = {
        atomicType: isInlineVideo ? DRAFT_ATOMIC_TYPE_INLINE_VIDEO : DRAFT_ATOMIC_TYPE_VIDEO,
        videoTitle: this.state.shouldInsertTitle ? name : undefined,
        url,
        thumbnailSrc: encodeUrl(thumbnail),
        uuid,
        customId: custom_id,
        width: DEFAULT_VIDEO_WIDTH,
        base_url
      };
      onChange(videoBlockMetadata);
      Tracker.track('prosemirrorVideo', {
        action: INSERT_HUBSPOT_VIDEO,
        method: 'toolbar',
        watchPageUrl: sharing_page,
        videoTitle: name,
        fileSource: 'vidyard'
      });
      callIfPossible(trackingHandlers.onInsertVideo);
      onClose();
    };
    this.state = {
      hasCreatedVideoOnce: false,
      isLoading: true,
      shouldInsertTitle: true
    };
  }
  componentDidMount() {
    this.fetchVidyardEmbedScript();
    this.handleInitiateVidyardComponent();
  }
  componentWillUnmount() {
    if (this.initiateComponentTimeout) {
      clearTimeout(this.initiateComponentTimeout);
    }
  }
  fetchVidyardEmbedScript() {
    if (!window.Vidyard) {
      const vidyardEmbedScript = document.createElement('script');
      vidyardEmbedScript.src = EMBED_SCRIPT_URL;
      if (document && document.body) {
        document.body.appendChild(vidyardEmbedScript);
      }
    }
  }
  render() {
    const {
      isLoading
    } = this.state;
    return /*#__PURE__*/_jsxs(View, {
      inlineSize: "100%",
      paddingBlockStart: "400",
      paddingBlockEnd: "1200",
      children: [this.props.showTitleCheckBox && /*#__PURE__*/_jsx(Checkbox, {
        defaultChecked: true,
        checked: this.state.shouldInsertTitle,
        onChange: ({
          target: {
            checked
          }
        }) => {
          this.setState({
            shouldInsertTitle: checked
          });
        },
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "draftPlugins.goVideoModal.showVideoTitle"
        })
      }), /*#__PURE__*/_jsx(Tile, {
        display: isLoading ? 'none' : 'block',
        distance: "flush",
        closeable: false,
        padding: "100",
        children: /*#__PURE__*/_jsx("div", {
          id: "GoVideo"
        })
      }), isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      })]
    });
  }
}
GoVideoContainer.propTypes = {
  isInServiceHub: PropTypes.bool,
  onChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  trackingHandlers: PropTypes.object.isRequired,
  recipientEmail: PropTypes.string,
  isInlineVideo: PropTypes.bool,
  showTitleCheckBox: PropTypes.bool
};
GoVideoContainer.defaultProps = {
  trackingHandlers: {},
  isInlineVideo: false,
  showTitleCheckBox: false
};
export default GoVideoContainer;